/**
 * CompanyMembersSection — widget "Uzytkownicy firmy" (silnik layoutu, typ elementu
 * `company_members_view`, patrz `components/modules/layout-elements/CompanyMembersViewElement.tsx`).
 *
 * Odwrotny widok do karty użytkownika → Firmy (`CompanyAccessRow`): firma × [osoby] zamiast
 * osoba × [firmy]. Każda osoba ma w firmie JEDNĄ LUB WIELE ról w kolejności (ADR-0103) — ten sam
 * edytor chipów co na karcie użytkownika (`CompanyRolesEditor`), do wyboru wyłącznie role TEJ
 * firmy. Każda zmiana ról to jedno żądanie „ustaw komplet ról" (`POST /companies/:id/members`).
 *
 * Dawny panel „dosypanych uprawnień" (rozwijany akordeon per moduł) zniknął — indywidualną
 * dosypkę serwer wycofał w F24 (każde kliknięcie kończyło się błędem), szczegółowe prawa nadaje
 * Centrum uprawnień.
 *
 * Dodanie wielu osób naraz idzie pętlą POST (nie `/companies/members/batch`, który ma odwrotną
 * oś: jedna osoba, wiele firm) — osobny wpis audytu per osoba, świadomie.
 *
 * Super-user: widget CELOWO pokazuje wyłącznie jawne przypisania z `user_company_access`.
 * Super-user ma dostęp do każdej firmy bezwarunkowo — jest nieobecny na liście i wykluczony
 * z pickera „dodaj użytkownika" (edytowalne role sugerowałyby fałszywie, że go ograniczają).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserPlus } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { IconButton } from '@/components/IconButton';
import { MultiSelect } from '@/components/MultiSelect';
import { Spinner } from '@/components/Spinner';
import { CompanyRolesEditor } from '@/components/users/CompanyRolesEditor';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import {
  COMPANY_MEMBERS_KEY,
  type CompanyMember,
  type RoleTemplate,
  useCompanyMembers,
  USER_PERMISSIONS_KEY,
  type UserListItem,
  useRoleTemplates,
  USERS_LIST_KEY,
  useUsersList,
} from '@/lib/use-permissions';
import { USER_DETAIL_KEY } from '@/lib/use-tenant-people';

export interface CompanyMembersSectionProps {
  companyId: string;
  /** `false` gdy karta firmy jest w trybie podgladu (nie edycji) — wiersze tylko do odczytu. */
  editable?: boolean;
}

/** Role osoby w firmie w kolejności — z pełnej listy, a u starszego serwera z roli głównej. */
function memberRoles(member: CompanyMember): Array<{ id: string; name: string }> {
  if (member.roles) return member.roles;
  return member.roleTemplateId
    ? [{ id: member.roleTemplateId, name: member.roleTemplateName ?? '' }]
    : [];
}

export function CompanyMembersSection({ companyId, editable = true }: CompanyMembersSectionProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const membersQ = useCompanyMembers(companyId);
  const rolesQ = useRoleTemplates();
  // Bez `companyId` — szukamy kandydatów SPOZA tej firmy (companyId zawęziłby wynik właśnie do
  // już-członków). `limit: 100` (backend default=25) — inaczej picker widziałby tylko pierwszą
  // stronę. Tenanty z >100 osobami: brak paginacji/wyszukiwania serwerowego (follow-up).
  const usersQ = useUsersList({ limit: 100 });

  const [showAddUser, setShowAddUser] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<CompanyMember | null>(null);

  function invalidateAfterWrite(...touchedUserIds: string[]) {
    queryClient.invalidateQueries({ queryKey: COMPANY_MEMBERS_KEY(companyId) });
    queryClient.invalidateQueries({ queryKey: USERS_LIST_KEY });
    for (const userId of touchedUserIds) {
      queryClient.invalidateQueries({ queryKey: USER_PERMISSIONS_KEY(userId) });
      // Karta usera niesie chipy firm w GET /users/:id/detail — bez tego zapis z poziomu firmy
      // nie odświeżyłby otwartej/zbuforowanej karty tej osoby.
      queryClient.invalidateQueries({ queryKey: USER_DETAIL_KEY(userId) });
    }
  }

  const setRolesMutation = useMutation({
    mutationFn: async (input: { userId: string; roleTemplateIds: string[] }) => {
      const res = await api.post(`/companies/${companyId}/members`, input);
      return res.data;
    },
    onSuccess: (_data, variables) => {
      invalidateAfterWrite(variables.userId);
      toast.show({ variant: 'success', title: t('tenant.permissions.toast.assignSuccess') });
    },
    onError: (err) => {
      toast.show({
        variant: 'error',
        title: t('tenant.permissions.toast.assignError'),
        description: extractErrorCode(err),
      });
    },
  });

  const batchAddMutation = useMutation({
    // Promise.allSettled (NIE Promise.all) — jedno odrzucone żądanie nie może ukryć już-udanych:
    // przy Promise.all cała mutacja by się odrzuciła i odświeżenie nigdy by nie ruszyło, mimo że
    // część osób faktycznie dostała dostęp na serwerze.
    mutationFn: async (input: { userIds: string[]; roleTemplateIds: string[] }) => {
      const results = await Promise.allSettled(
        input.userIds.map((userId) =>
          api.post(`/companies/${companyId}/members`, {
            userId,
            roleTemplateIds: input.roleTemplateIds,
          }),
        ),
      );
      const succeededUserIds = input.userIds.filter((_, i) => results[i]?.status === 'fulfilled');
      return { succeededUserIds, total: input.userIds.length };
    },
    onSuccess: ({ succeededUserIds, total }) => {
      if (succeededUserIds.length > 0) invalidateAfterWrite(...succeededUserIds);
      if (succeededUserIds.length === total) {
        toast.show({ variant: 'success', title: t('tenant.permissions.toast.assignSuccess') });
      } else if (succeededUserIds.length === 0) {
        toast.show({ variant: 'error', title: t('tenant.permissions.toast.assignError') });
      } else {
        toast.show({
          variant: 'warning',
          title: t('tenant.companies.members.toast.partialAdd', {
            success: succeededUserIds.length,
            total,
          }),
        });
      }
    },
    onError: (err) => {
      // Nieosiągalne w praktyce — Promise.allSettled nigdy nie odrzuca całej mutacji.
      toast.show({
        variant: 'error',
        title: t('tenant.permissions.toast.assignError'),
        description: extractErrorCode(err),
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const res = await api.delete(`/companies/${companyId}/members/${userId}`);
      return res.data;
    },
    onSuccess: (_data, userId) => {
      invalidateAfterWrite(userId);
      toast.show({ variant: 'success', title: t('tenant.permissions.toast.revokeSuccess') });
      setRevokeTarget(null);
    },
    onError: (err) => {
      setRevokeTarget(null);
      if (extractErrorCode(err) === 'LAST_COMPANY_ACCESS') {
        toast.show({
          variant: 'warning',
          title: t('tenant.permissions.toast.lastCompany.title'),
          description: t('tenant.permissions.toast.lastCompany.description'),
          duration: 7000,
        });
        return;
      }
      toast.show({
        variant: 'error',
        title: t('tenant.permissions.toast.revokeError'),
        description: extractErrorCode(err),
      });
    },
  });

  const members = membersQ.data?.members ?? [];
  // Role są per firma — do wyboru wyłącznie role TEJ firmy.
  const companyRoles = useMemo(
    () => (rolesQ.data?.roleTemplates ?? []).filter((r) => r.companyId === companyId),
    [rolesQ.data, companyId],
  );
  // Lista użytkowników tenanta wczytana — dopiero wtedy wiadomo, czy jest kogo dodać (wcześniej
  // pusta lista w trakcie ładowania dawała fałszywe „wszyscy mają już dostęp").
  const usersLoaded = usersQ.isSuccess;
  const allUsers = usersQ.data?.users ?? [];
  const availableUsers = allUsers.filter(
    (u) => !u.isSuperUser && !members.some((m) => m.userId === u.id),
  );

  const busy = setRolesMutation.isPending || batchAddMutation.isPending || revokeMutation.isPending;

  // rolesQ objęty tym samym gate'em co membersQ — bez tego padnięte /role-templates dawałoby
  // edytor ról bez żadnej roli do wyboru i bez sygnału, że słownik się nie załadował.
  if (membersQ.isLoading || rolesQ.isLoading) {
    return (
      <div className="flex items-center justify-center py-4" role="status">
        <Spinner />
      </div>
    );
  }

  if (membersQ.isError || rolesQ.isError) {
    return (
      <p className="text-sm text-red-600" data-testid="company-members-error">
        {t('tenant.companies.members.loadError')}
      </p>
    );
  }

  return (
    <section data-testid="company-members-section">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('tenant.companies.members.title')}
      </h3>

      <div className="space-y-2 text-sm">
        {/* Jeden komunikat zamiast dwóch sprzecznych („nikt nie ma dostępu" + „wszyscy mają"):
            pusta firma bez nikogo do dodania (np. sami superużytkownicy, którzy i tak widzą
            wszystkie firmy) dostaje własny opis. */}
        {members.length === 0 && !showAddUser && (
          <p className="text-sm text-text-muted" data-testid="company-members-empty">
            {editable && usersLoaded && availableUsers.length === 0
              ? t('tenant.companies.members.emptyNoneToAdd')
              : t('tenant.companies.members.empty')}
          </p>
        )}

        {members.map((member) => (
          <CompanyMemberRow
            key={member.userId}
            companyId={companyId}
            member={member}
            companyRoles={companyRoles}
            editable={editable}
            busy={busy}
            onRolesChange={(roleTemplateIds) =>
              setRolesMutation.mutate({ userId: member.userId, roleTemplateIds })
            }
            onRevoke={() => setRevokeTarget(member)}
          />
        ))}

        {editable &&
          (showAddUser ? (
            <AddUserForm
              companyId={companyId}
              available={availableUsers}
              companyRoles={companyRoles}
              onCancel={() => setShowAddUser(false)}
              onSubmit={(userIds, roleTemplateIds) => {
                batchAddMutation.mutate({ userIds, roleTemplateIds });
                setShowAddUser(false);
              }}
            />
          ) : usersQ.isError ? (
            <p className="text-xs text-red-600" data-testid="company-members-users-error">
              {t('tenant.companies.members.loadError')}
            </p>
          ) : !usersLoaded ? null : availableUsers.length > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAddUser(true)}
              leftIcon={<UserPlus size={14} aria-hidden="true" />}
              data-testid="company-members-add-btn"
            >
              {t('tenant.companies.members.addUser')}
            </Button>
          ) : members.length > 0 ? (
            <p className="text-xs text-text-tertiary" data-testid="company-members-no-available">
              {t('tenant.companies.members.addUserForm.noAvailable')}
            </p>
          ) : null)}
      </div>

      <ConfirmDialog
        open={!!revokeTarget}
        title={t('tenant.companies.members.confirmRevoke.title')}
        message={t('tenant.companies.members.confirmRevoke.message', {
          name: revokeTarget ? (revokeTarget.displayName?.trim() ?? revokeTarget.email) : '',
        })}
        variant="danger"
        confirming={revokeMutation.isPending}
        onConfirm={() => revokeTarget && revokeMutation.mutate(revokeTarget.userId)}
        onClose={() => setRevokeTarget(null)}
      />
    </section>
  );
}

// ============================================================================
// CompanyMemberRow
// ============================================================================

function CompanyMemberRow({
  companyId,
  member,
  companyRoles,
  editable,
  busy,
  onRolesChange,
  onRevoke,
}: {
  companyId: string;
  member: CompanyMember;
  companyRoles: RoleTemplate[];
  editable: boolean;
  busy: boolean;
  onRolesChange: (roleTemplateIds: string[]) => void;
  onRevoke: () => void;
}) {
  const { t } = useTranslation();
  const label = member.displayName?.trim() ? member.displayName : member.email;
  const roles = memberRoles(member);

  return (
    <div
      className="rounded-lg border border-border-secondary p-3"
      data-testid={`company-member-${member.userId}`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-medium text-text-primary">{label}</span>
            {member.status === 'suspended' && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                {t('tenant.permissions.badge.suspended')}
              </span>
            )}
          </div>
          {member.displayName && (
            <div className="truncate text-xs text-text-tertiary">{member.email}</div>
          )}
        </div>
        {editable ? (
          <CompanyRolesEditor
            companyId={companyId}
            testIdKey={member.userId}
            assigned={roles}
            available={companyRoles}
            onChange={onRolesChange}
            busy={busy}
          />
        ) : (
          // Podgląd karty firmy: role jako tekst — bez przeciągania i krzyżyków.
          <span
            className="text-xs text-text-secondary"
            data-testid={`member-roles-${member.userId}`}
          >
            {roles.length > 0 ? roles.map((r) => r.name).join(', ') : t('tenant.users.roles.none')}
          </span>
        )}
        {editable && (
          <IconButton
            variant="danger"
            size="sm"
            onClick={onRevoke}
            disabled={busy}
            aria-label={t('tenant.companies.members.actions.revokeUser')}
            data-testid={`member-revoke-${member.userId}`}
          >
            <Trash2 aria-hidden="true" />
          </IconButton>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// AddUserForm — picker osób + role (wiele, w kolejności)
// ============================================================================

function AddUserForm({
  companyId,
  available,
  companyRoles,
  onCancel,
  onSubmit,
}: {
  companyId: string;
  available: UserListItem[];
  companyRoles: RoleTemplate[];
  onCancel: () => void;
  onSubmit: (userIds: string[], roleTemplateIds: string[]) => void;
}) {
  const { t } = useTranslation();
  const defaultRole =
    companyRoles.find((r) => r.isDefaultMember) ??
    companyRoles.find((r) => r.isSystem) ??
    companyRoles[0];
  const [userIds, setUserIds] = useState<string[]>([]);
  const [roleIds, setRoleIds] = useState<string[]>(defaultRole ? [defaultRole.id] : []);

  const nameById = new Map(companyRoles.map((r) => [r.id, r.name]));
  const allSelected = available.length > 0 && userIds.length === available.length;
  const canSubmit = userIds.length > 0 && roleIds.length > 0;

  return (
    <form
      className="flex flex-col gap-3 rounded-lg border border-dashed border-accent-300 bg-accent-50/50 p-3 dark:bg-accent-900/10"
      data-testid="company-members-add-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) onSubmit(userIds, roleIds);
      }}
    >
      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="block text-xs text-text-tertiary">
            {t('tenant.companies.members.addUserForm.userLabel')}
          </label>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto p-0 text-xs hover:bg-transparent hover:underline"
            disabled={available.length === 0}
            data-testid="company-members-select-all"
            onClick={() => setUserIds(allSelected ? [] : available.map((u) => u.id))}
          >
            {allSelected
              ? t('tenant.permissions.addCompany.clearAll')
              : t('tenant.permissions.addCompany.selectAll')}
          </Button>
        </div>
        <MultiSelect
          value={userIds}
          onChange={setUserIds}
          options={available.map((u) => ({
            value: u.id,
            label: u.displayName?.trim() ? u.displayName : u.email,
          }))}
          label={undefined}
          data-testid="company-members-add-select"
        />
      </div>
      <div>
        <label className="mb-1 block text-xs text-text-tertiary">
          {t('tenant.companies.members.addUserForm.rolesLabel')}
        </label>
        <CompanyRolesEditor
          companyId={companyId}
          testIdKey={`${companyId}-new`}
          assigned={roleIds.map((id) => ({ id, name: nameById.get(id) ?? '' }))}
          available={companyRoles}
          onChange={setRoleIds}
        />
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!canSubmit}
          data-testid="company-members-add-submit"
        >
          {t('tenant.permissions.addCompany.submit')}
        </Button>
      </div>
    </form>
  );
}
